import { RefObject, useEffect } from 'react';

import { DataDogLogger } from '../../../hooks/useDataDogLogger';

type Options = {
  /**
   * Master toggle. When false, the hook becomes a no-op and emits zero DD
   * events. Use this to gate telemetry in preview/test environments where
   * branding config is intentionally placeholder. Default: true.
   */
  enabled?: boolean;

  /** Tier 1 — DOM presence + content + size + CSS visibility (defaults: all on) */
  /**
   * Verifies the ref's element exists after mount. When true and `ref.current`
   * is null, fires a `MISSING_DOM` error.
   *
   * Set to `false` when the ref lives on a single branch of a multi-branch
   * render (e.g. a template whose error/loading/success screens each return a
   * different root). On the branches where that element is not mounted the ref
   * is intentionally null, so leaving this on would emit a false `MISSING_DOM`
   * every time another branch renders.
   */
  checkPresence?: boolean;
  checkContent?: boolean;
  checkSize?: boolean;
  checkCss?: boolean;

  /**
   * DOM-walking WCAG contrast check. For every element inside the ref that
   * matches `textSelectors` (and is not inside an `excludeSelectors` ancestor),
   * the hook reads the actually-rendered text color and effective background
   * color from the DOM and computes the WCAG contrast ratio. Detects styling
   * issues regardless of source — CSS overrides, theme switches, inline
   * styles, BE branding bugs, etc.
   */
  checkContrast?: boolean;
  /** CSS selectors for text-bearing elements to inspect. */
  textSelectors?: string[];
  /** CSS selectors whose descendants should be skipped (e.g. buttons). */
  excludeSelectors?: string[];
  /** Below this ratio, contrast is reported as INVISIBLE_TEXT. Default 1.5. */
  contrastThreshold?: number;

  /** Tier 3 — overflow clipping and font load */
  checkClipped?: boolean;
  checkFont?: boolean;

  /** Logger from useDataDogLogger / useDataDogActions */
  logger: DataDogLogger;
  /** DD action name to emit (e.g. DATADOG_ACTIONS.errors.template_error) */
  errorAction: string;
  /** Identifies the render slot in DD ctx (e.g. 'render') */
  flow: string;
};

const DEFAULT_TEXT_SELECTORS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  '[role="textbox"]',
  '.c-GoogleFontViewer',
];

const DEFAULT_EXCLUDE_SELECTORS = ['button', 'a', 'footer', '[role="button"]'];

const parseRgbString = (color: string): [number, number, number] | null => {
  const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  return m ? [+m[1], +m[2], +m[3]] : null;
};

const getEffectiveBackground = (el: HTMLElement): string => {
  let current: HTMLElement | null = el;
  while (current) {
    const bg = getComputedStyle(current).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      return bg;
    }
    current = current.parentElement;
  }
  return 'rgb(255, 255, 255)';
};

const luminance = ([r, g, b]: [number, number, number]): number => {
  const [rs, gs, bs] = [r, g, b].map(v => {
    const n = v / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const contrastRatio = (
  c1: [number, number, number],
  c2: [number, number, number],
): number => {
  const l1 = luminance(c1);
  const l2 = luminance(c2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

/** Build a short, debuggable selector string for an element. */
const describeElement = (el: Element): string => {
  const tag = el.tagName.toLowerCase();
  const firstClass = el.classList[0];
  return firstClass ? `${tag}.${firstClass}` : tag;
};

/**
 * Runs a battery of render quality checks on the element pointed to by the
 * provided ref and reports each failure as a discrete DataDog error. Combines:
 *
 * 1. DOM-based render checks — detect missing DOM, empty content, zero size,
 *    CSS-hidden, clipped text, font load.
 *
 * 2. DOM-walking WCAG contrast check — finds every text-bearing element inside
 *    the ref (matched by `textSelectors`), skips elements inside excluded
 *    ancestors (`excludeSelectors`), and validates each one's rendered color
 *    against its effective background. Detects styling regressions regardless
 *    of source (CSS, theme switches, BE branding, inline overrides).
 *
 * Each failed check fires its own DD error with a distinct `errorCode`,
 * letting dashboards group by category and prioritize fixes.
 *
 * @example
 * // Basic usage: all defaults on, gate via preview flag
 * const containerRef = useRef<HTMLElement>(null);
 *
 * useVisibilityCheck(containerRef, {
 *   enabled: !isPortalPreview,
 *   logger: dataDogLogger,
 *   errorAction: DATADOG_ACTIONS.errors.template_error,
 *   flow: 'render',
 * });
 *
 * return <MyComponent containerRef={containerRef} />;
 *
 * @example
 * // Only run the contrast check (skip all other DOM checks)
 * useVisibilityCheck(containerRef, {
 *   enabled: !isPortalPreview,
 *   logger: dataDogLogger,
 *   errorAction: DATADOG_ACTIONS.errors.template_error,
 *   flow: 'render',
 *   checkPresence: false,
 *   checkContent: false,
 *   checkSize: false,
 *   checkCss: false,
 *   checkClipped: false,
 *   checkFont: false,
 *   checkContrast: true,
 * });
 *
 * @example
 * // Custom selectors — limit contrast inspection to the title and exclude
 * // header/footer regions in addition to the defaults
 * useVisibilityCheck(containerRef, {
 *   enabled: !isPortalPreview,
 *   logger: dataDogLogger,
 *   errorAction: DATADOG_ACTIONS.errors.template_error,
 *   flow: 'render_title',
 *   textSelectors: ['h1.c-MyTemplate__title'],
 *   excludeSelectors: ['button', 'a', 'header', 'footer'],
 * });
 *
 * @example
 * // Stricter contrast threshold — flag anything below WCAG AA Large (3:1)
 * useVisibilityCheck(containerRef, {
 *   enabled: !isPortalPreview,
 *   logger: dataDogLogger,
 *   errorAction: DATADOG_ACTIONS.errors.template_error,
 *   flow: 'render',
 *   contrastThreshold: 3,
 * });
 *
 * @example
 * // No DOM ref needed: pass `null` and only the master toggle / logger fields
 * // matter. Useful if you eventually re-add a non-DOM check (e.g. validating
 * // BE config directly). Today this is a no-op since every other check needs
 * // a ref to walk.
 * useVisibilityCheck(null, {
 *   enabled: !isPortalPreview,
 *   logger: dataDogLogger,
 *   errorAction: DATADOG_ACTIONS.errors.template_error,
 *   flow: 'render',
 * });
 *
 * @example
 * // Emitted DD error payload shape for INVISIBLE_TEXT
 * // {
 * //   flow: 'render',
 * //   statusCode: 0,
 * //   errorCode: 'INVISIBLE_TEXT',
 * //   errorMessage: 'Low contrast on "h1.c-Title": 1.00 (text: rgb(255,255,255), bg: rgb(255,255,255))',
 * //   selector: 'h1.c-Title',
 * //   textPreview: 'Terms and Conditions',
 * //   contrastRatio: 1,
 * //   textColor: 'rgb(255, 255, 255)',
 * //   backgroundColor: 'rgb(255, 255, 255)',
 * // }
 */
export const useVisibilityCheck = (
  ref: RefObject<HTMLElement> | null | undefined,
  {
    enabled = true,
    checkPresence = true,
    checkContent = true,
    checkSize = true,
    checkCss = true,
    checkContrast = true,
    textSelectors = DEFAULT_TEXT_SELECTORS,
    excludeSelectors = DEFAULT_EXCLUDE_SELECTORS,
    contrastThreshold = 1.5,
    checkClipped = true,
    checkFont = true,
    logger,
    errorAction,
    flow,
  }: Options,
) => {
  useEffect(() => {
    if (!enabled) return;
    if (ref == null) return;

    const el = ref.current;

    if (checkPresence && !el) {
      logger.error(errorAction, {
        flow,
        statusCode: 0,
        errorCode: 'MISSING_DOM',
        errorMessage: 'Element ref is null after mount',
      });
      return;
    }

    if (!el) return;

    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      const styles = getComputedStyle(el);
      const text = el.textContent?.trim() ?? '';

      if (checkContent && text === '') {
        logger.warning(errorAction, {
          flow,
          statusCode: 0,
          errorCode: 'EMPTY_CONTENT',
          errorMessage: 'Element has no textContent after mount',
        });
      }

      if (checkSize && (el.offsetWidth === 0 || el.offsetHeight === 0)) {
        logger.error(errorAction, {
          flow,
          statusCode: 0,
          errorCode: 'ZERO_SIZE',
          errorMessage: `Element has zero dimensions: ${el.offsetWidth}x${el.offsetHeight}`,
        });
      }

      if (checkCss) {
        const hidden =
          styles.display === 'none' ||
          styles.visibility === 'hidden' ||
          parseFloat(styles.opacity) === 0;
        if (hidden) {
          logger.error(errorAction, {
            flow,
            statusCode: 0,
            errorCode: 'CSS_HIDDEN',
            errorMessage: `Hidden by CSS: display=${styles.display}, visibility=${styles.visibility}, opacity=${styles.opacity}`,
          });
        }
      }

      if (checkClipped) {
        const overflowHidden =
          styles.overflow === 'hidden' ||
          styles.overflowX === 'hidden' ||
          styles.overflowY === 'hidden';
        const clipped =
          overflowHidden &&
          (el.scrollWidth > el.clientWidth ||
            el.scrollHeight > el.clientHeight);
        if (clipped) {
          logger.error(errorAction, {
            flow,
            statusCode: 0,
            errorCode: 'TEXT_CLIPPED',
            errorMessage: `Text overflowing hidden bounds: scroll=${el.scrollWidth}x${el.scrollHeight}, client=${el.clientWidth}x${el.clientHeight}`,
          });
        }
      }

      if (checkFont && document.fonts) {
        const fontFamily = styles.fontFamily;

        void document.fonts.ready.then(() => {
          if (cancelled) return;
          try {
            if (!document.fonts.check(fontFamily)) {
              logger.error(errorAction, {
                flow,
                statusCode: 0,
                errorCode: 'FONT_NOT_LOADED',
                errorMessage: `Font not loaded: ${fontFamily}`,
                fontFamily,
              });
            }
          } catch {
            // ignore invalid font shorthand
          }
        });
      }

      if (checkContrast && textSelectors.length > 0) {
        const textQuery = textSelectors.join(',');
        const excludeQuery = excludeSelectors.join(',');
        const candidates = el.querySelectorAll<HTMLElement>(textQuery);

        candidates.forEach(textEl => {
          if (excludeQuery && textEl.closest(excludeQuery)) {
            return;
          }
          const innerText = textEl.textContent?.trim() ?? '';
          if (innerText === '') return;

          const textColor = getComputedStyle(textEl).color;
          const bgColor = getEffectiveBackground(textEl);
          const textRgb = parseRgbString(textColor);
          const bgRgb = parseRgbString(bgColor);
          if (!textRgb || !bgRgb) return;

          const ratio = contrastRatio(textRgb, bgRgb);
          if (ratio < contrastThreshold) {
            logger.error(errorAction, {
              flow,
              statusCode: 0,
              errorCode: 'INVISIBLE_TEXT',
              errorMessage: `Low contrast on "${describeElement(textEl)}"`,
              selector: describeElement(textEl),
              textPreview: innerText.slice(0, 80),
              contrastRatio: ratio,
              textColor,
              backgroundColor: bgColor,
            });
          }
        });
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [
    enabled,
    ref,
    checkPresence,
    checkContent,
    checkSize,
    checkCss,
    checkContrast,
    textSelectors,
    excludeSelectors,
    contrastThreshold,
    checkClipped,
    checkFont,
    logger,
    errorAction,
    flow,
  ]);
};
